/**
 * Turn FastAPI/axios errors into a safe string for toasts (never pass raw `detail` arrays to React/sonner).
 * Works with axios errors, or plain `{ detail }` from `await res.json()` on failed fetch.
 */
export function formatApiError(error, fallback = "Something went wrong") {
  const d =
    error?.response?.data?.detail !== undefined
      ? error.response.data.detail
      : error && typeof error === "object" && error.detail !== undefined
        ? error.detail
        : undefined;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return (
      d
        .map((item) => {
          if (item && typeof item === "object" && typeof item.msg === "string") {
            return item.msg;
          }
          return null;
        })
        .filter(Boolean)
        .join(" ") || fallback
    );
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  const noHttpResponse = !error?.response && (error?.code === "ERR_NETWORK" || error?.message === "Network Error");
  if (noHttpResponse) {
    return "Could not reach the server. Try again, or reload the page.";
  }
  if (typeof error?.message === "string" && error.message) return error.message;
  return fallback;
}

export function isTransientRequestError(error) {
  const status = error?.response?.status;
  return (
    !error?.response ||
    status === 502 ||
    status === 503 ||
    status === 504 ||
    error?.code === "ECONNABORTED" ||
    error?.code === "ERR_NETWORK"
  );
}

export function shouldRetryIdempotentRequest(
  error,
  { method = "get", retryCount = 0, maxRetries = 2 } = {}
) {
  const m = String(method || "get").toLowerCase();
  if (m !== "get" && m !== "head") return false;
  if (retryCount >= maxRetries) return false;
  const status = error?.response?.status;
  if (status === 401 || status === 403 || status === 404) return false;
  return isTransientRequestError(error);
}
